import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDownIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleHelpIcon,
  CopyIcon,
  FileTextIcon,
  LoaderCircleIcon,
  RefreshCwIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { Fragment, useState, type ReactNode } from "react";
import { toast } from "sonner";
import type { Box31Criterion, Box31Entry, Box31Generation, Box31Kind, Box31Placeholder, Box31Suggestion, Box31Version, Phase, SessionDetail, TurnStep } from "../../../../shared/api.ts";
import { formatCode } from "../../../../shared/uktzed.ts";
import { stepIcon } from "@/components/turn/step-icon.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible.tsx";
import { ConfirmDialog } from "@/components/ui/confirm-dialog.tsx";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.tsx";
import { Segmented } from "@/components/ui/segmented.tsx";
import { TabCount } from "@/components/ui/tabs.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { formatCodesSafely } from "@/lib/codes.ts";
import { errorMessage, useNow } from "@/lib/hooks.ts";
import { useTurnStatus } from "@/lib/turn-stream.ts";
import { isActiveStatus } from "@/lib/turns.ts";
import { cn, copyText, elapsedMs } from "@/lib/utils.ts";
import { CitationQuote } from "./citations.tsx";

const TOKEN = /(\[[^[\]]*\])/g;

/** The placeholders a version holds, in the order the suggestion lists them. */
function placeholdersIn(version: Box31Version, placeholders: Box31Placeholder[]): Box31Placeholder[] {
  return placeholders.filter((placeholder) => version.text.includes(placeholder.token));
}

/** The text with its placeholders marked: amber when required, blue when recommended. */
function MarkedText({ text, placeholders }: { text: string; placeholders: Box31Placeholder[] }) {
  return text.split(TOKEN).map((part, index) => {
    if (index % 2 === 0) return <Fragment key={index}>{part}</Fragment>;
    const required = placeholders.find((placeholder) => placeholder.token === part)?.required ?? false;
    return (
      <mark
        key={index}
        className={cn(
          "rounded-[4px] px-0.5 font-semibold",
          required ? "bg-warning-soft text-warning ring-1 ring-warning-border" : "bg-primary-soft text-primary-soft-foreground",
        )}
      >
        {part}
      </mark>
    );
  });
}

function VersionPanel({ version, placeholders, onCopy }: { version: Box31Version; placeholders: Box31Placeholder[]; onCopy: () => void }) {
  const { t } = useI18n();
  const over = version.length > version.limit;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="min-w-0 flex-1 text-[12px] text-muted-foreground">{t.box31.kindHints[version.kind]}</span>
        <span
          className={cn("code-mono shrink-0 text-[12px]", over ? "font-semibold text-danger" : "text-muted-foreground")}
          aria-label={t.box31.length(version.length, version.limit)}
          title={t.box31.length(version.length, version.limit)}
        >
          {version.length}/{version.limit}
        </span>
        <Button variant="outline" size="xs" onClick={onCopy}>
          <CopyIcon aria-hidden />
          {t.box31.copy}
        </Button>
      </div>
      <p lang="uk" className="rounded-lg border bg-muted/40 px-3.5 py-3 text-[14px] leading-relaxed break-words text-foreground">
        <MarkedText text={version.text} placeholders={placeholders} />
      </p>
    </div>
  );
}

function PlaceholderList({ placeholders }: { placeholders: Box31Placeholder[] }) {
  const { t } = useI18n();
  if (placeholders.length === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <h4 className="text-[12px] font-semibold text-muted-foreground">{t.box31.placeholders}</h4>
      <ul className="flex flex-col gap-2">
        {placeholders.map((placeholder) => (
          <li key={placeholder.token} className="rounded-lg border px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <span lang="uk" className="code-mono text-[12.5px] font-semibold break-all">
                {placeholder.token}
              </span>
              <Badge variant={placeholder.required ? "warning" : "primary"}>{placeholder.required ? t.box31.required : t.box31.recommended}</Badge>
            </div>
            <dl className="mt-2 grid grid-cols-[minmax(6.5rem,auto)_minmax(0,1fr)] gap-x-3 gap-y-1 text-[12.5px] leading-relaxed">
              <dt className="text-muted-foreground">{t.box31.what}</dt>
              <dd lang="uk">{placeholder.label}</dd>
              {placeholder.why ? (
                <>
                  <dt className="text-muted-foreground">{t.box31.why}</dt>
                  <dd lang="uk">{placeholder.why}</dd>
                </>
              ) : null}
              {placeholder.where ? (
                <>
                  <dt className="text-muted-foreground">{t.box31.where}</dt>
                  <dd lang="uk">{placeholder.where}</dd>
                </>
              ) : null}
            </dl>
          </li>
        ))}
      </ul>
    </section>
  );
}

const CRITERION_ICONS: Record<Box31Criterion["status"], { icon: typeof CircleCheckIcon; className: string }> = {
  confirmed: { icon: CircleCheckIcon, className: "text-success" },
  missing: { icon: CircleHelpIcon, className: "text-warning" },
  conflict: { icon: CircleAlertIcon, className: "text-danger" },
};

function CriteriaList({ criteria, defaultOpen }: { criteria: Box31Criterion[]; defaultOpen: boolean }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(defaultOpen);
  if (criteria.length === 0) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-lg border">
      <CollapsibleTrigger className="group flex w-full items-center gap-2 px-3 py-2.5 text-left text-[12.5px] font-semibold outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35">
        {t.box31.criteria}
        <TabCount value={criteria.length} />
        <ChevronDownIcon className="ml-auto size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" aria-hidden />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="divide-y border-t">
          {criteria.map((criterion, index) => {
            const { icon: Icon, className } = CRITERION_ICONS[criterion.status];
            return (
              <li key={index} className="flex gap-2.5 px-3 py-2.5 text-[12.5px] leading-relaxed">
                <Icon className={cn("mt-0.5 size-4 shrink-0", className)} aria-label={t.box31.criterionStatus[criterion.status]} />
                <div lang="uk" className="min-w-0">
                  <p>
                    <span className="code-mono font-semibold">{criterion.level}</span>
                    {criterion.against ? <span className="text-muted-foreground"> · {criterion.against}</span> : null}
                  </p>
                  <p>{criterion.criterion}</p>
                  {criterion.fact ? <p className="text-muted-foreground">{criterion.fact}</p> : null}
                  {criterion.status === "confirmed" && criterion.citations && criterion.citations.length > 0 ? (
                    <ul lang="" className="mt-1.5 flex flex-col gap-2" aria-label={t.box31.quotes}>
                      {criterion.citations.map((citation, quote) => (
                        <li key={`${citation.source}:${quote}`}>
                          <CitationQuote citation={citation} />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

function Versions({ suggestion }: { suggestion: Box31Suggestion }) {
  const { t } = useI18n();
  const [kind, setKind] = useState<Box31Kind>("standard");
  const [confirming, setConfirming] = useState<Box31Version | null>(null);
  const version = suggestion.versions.find((item) => item.kind === kind) ?? suggestion.versions[0];
  if (!version) return null;
  const inVersion = placeholdersIn(version, suggestion.placeholders);

  const copy = async (target: Box31Version) => {
    if (await copyText(target.text)) {
      toast.success(t.box31.copied, { description: `${t.box31.kinds[target.kind]} · ${t.box31.length(target.length, target.limit)}` });
    } else {
      toast.error(t.box31.copyFailed);
    }
  };
  const requestCopy = (target: Box31Version) => {
    if (placeholdersIn(target, suggestion.placeholders).some((placeholder) => placeholder.required)) {
      setConfirming(target);
    } else {
      void copy(target);
    }
  };
  const requiredTokens = confirming
    ? placeholdersIn(confirming, suggestion.placeholders).filter((placeholder) => placeholder.required).map((placeholder) => placeholder.token)
    : [];

  return (
    <div className="flex flex-col gap-4">
      <Segmented
        value={kind}
        onValueChange={setKind}
        label={t.box31.versionLabel}
        className="flex w-full"
        options={suggestion.versions.map((item) => ({ value: item.kind, label: t.box31.kinds[item.kind] }))}
      />
      <VersionPanel version={version} placeholders={suggestion.placeholders} onCopy={() => requestCopy(version)} />
      <PlaceholderList placeholders={inVersion} />
      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={t.box31.copyWarnTitle}
        description={t.box31.copyWarnText(requiredTokens.join(", "))}
        confirmLabel={t.box31.copyAnyway}
        cancelLabel={t.common.cancel}
        destructive={false}
        onConfirm={() => {
          if (confirming) void copy(confirming);
          setConfirming(null);
        }}
      />
    </div>
  );
}

const STEPS_SHOWN = 5;

/**
 * The latest steps of a running generation, without its reasoning: the case-file searches, and the lookup's web
 * searches and pages when the case file left a decisive fact missing.
 */
function GenerationSteps({ steps }: { steps: TurnStep[] }) {
  const { t } = useI18n();
  const shown = steps.filter((step) => step.kind !== "reasoning").slice(-STEPS_SHOWN);
  if (shown.length === 0) return null;
  return (
    <ol className="flex flex-col gap-1" aria-label={t.box31.steps}>
      {shown.map((step) => {
        const Icon = stepIcon(step);
        return (
          <li key={step.id} className="flex min-w-0 items-center gap-2 text-[12.5px] text-foreground/85">
            {step.status === "running" ? (
              <LoaderCircleIcon className="size-3.5 shrink-0 animate-spin text-primary" aria-label={t.turn.stepRunning} />
            ) : (
              <Icon className={cn("size-3.5 shrink-0", step.status === "failed" ? "text-danger" : "text-muted-foreground")} aria-hidden />
            )}
            <span className="sr-only">{t.turn.kinds[step.kind]}: </span>
            <span className={cn("min-w-0 truncate", step.status === "failed" && "text-danger")} title={step.label}>
              {formatCodesSafely(step.label)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** A coloured box with an icon: progress, failure, outdated texts, a conflict. */
function Notice({ tone, icon, title, children }: { tone: "info" | "danger" | "warning"; icon: ReactNode; title: ReactNode; children?: ReactNode }) {
  return (
    <div
      role={tone === "info" ? "status" : undefined}
      className={cn(
        "flex gap-2.5 rounded-xl border px-3.5 py-3 text-[13px] leading-relaxed",
        tone === "info" && "bg-primary-soft/50",
        tone === "danger" && "border-danger-border bg-danger-soft",
        tone === "warning" && "border-warning-border bg-warning-soft",
      )}
    >
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className={cn("font-semibold", tone === "danger" && "text-danger", tone === "warning" && "text-warning")}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function Box31Dialog({
  open,
  onOpenChange,
  code,
  entry,
  generation,
  progress,
  readOnly,
  onGenerate,
  starting,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  code: string;
  entry: Box31Entry | null;
  generation: Box31Generation | null;
  /** What the running generation is doing, from its events. */
  progress: { phase: Phase | null; steps: TurnStep[] } | null;
  readOnly: boolean;
  onGenerate: () => void;
  starting: boolean;
}) {
  const { t, fmt } = useI18n();
  const running = generation !== null && isActiveStatus(generation.status);
  const now = useNow(open && generation?.status === "running");
  const suggestion = entry?.suggestion ?? null;
  const failed = generation !== null && !running && generation.status !== "succeeded";
  const busy = running || starting;
  // The researcher's calls run in phases of their own while the generation waits for them.
  const lookingUp = progress?.phase === "evidence" || progress?.phase === "lookup";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t.common.close} className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t.box31.titleFor(formatCode(code))}</DialogTitle>
          <DialogDescription>{t.box31.description}</DialogDescription>
        </DialogHeader>

        {running ? (
          <Notice
            tone="info"
            icon={<LoaderCircleIcon className="size-4 animate-spin text-primary" aria-hidden />}
            title={
              <span className="flex items-center gap-2">
                <span className="flex-1">{generation.status === "queued" ? t.box31.progressQueued : lookingUp ? t.box31.progressLookup : t.box31.progress}</span>
                {generation.status === "running" ? (
                  <span className="code-mono font-medium">{fmt.clock(elapsedMs(generation.startedAt, null, now) ?? 0)}</span>
                ) : null}
              </span>
            }
          >
            {generation.status === "running" && progress ? <GenerationSteps steps={progress.steps} /> : null}
            {suggestion ? <p className="text-[12.5px] text-muted-foreground">{t.box31.progressKeep}</p> : null}
          </Notice>
        ) : null}

        {failed ? (
          <Notice
            tone="danger"
            icon={<CircleAlertIcon className="size-4 text-danger" aria-hidden />}
            title={generation.status === "cancelled" ? t.box31.stopped : t.box31.failed}
          >
            {generation.error && generation.status !== "cancelled" ? <p className="text-[12.5px] break-words text-foreground/85">{generation.error}</p> : null}
            {!readOnly ? (
              <Button variant="outline" size="xs" className="self-start" disabled={busy} onClick={onGenerate}>
                <RefreshCwIcon aria-hidden />
                {t.box31.tryAgain}
              </Button>
            ) : null}
          </Notice>
        ) : null}

        {suggestion && entry?.outdated && !running ? (
          <Notice tone="warning" icon={<TriangleAlertIcon className="size-4 text-warning" aria-hidden />} title={t.box31.stale} />
        ) : null}

        {suggestion?.status === "conflict" ? (
          <Notice tone="danger" icon={<CircleAlertIcon className="size-4 text-danger" aria-hidden />} title={t.box31.conflictTitle}>
            <p lang="uk" className="break-words text-foreground">{suggestion.conflict}</p>
          </Notice>
        ) : null}

        {suggestion?.status === "ready" ? <Versions suggestion={suggestion} /> : null}

        {!suggestion && !running && !failed ? (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed px-4 py-7 text-center">
            <FileTextIcon className="size-5 text-muted-foreground" aria-hidden />
            <p className="text-[13px] font-semibold">{t.box31.empty}</p>
            <p className="max-w-sm text-[12.5px] text-muted-foreground">{t.box31.emptyHint}</p>
            {!readOnly ? (
              <Button size="sm" className="mt-1" disabled={busy} onClick={onGenerate}>
                {t.box31.generate}
              </Button>
            ) : null}
          </div>
        ) : null}

        {suggestion && suggestion.warnings.length > 0 ? (
          <Notice tone="warning" icon={<TriangleAlertIcon className="size-4 text-warning" aria-hidden />} title={t.box31.warnings}>
            <ul lang="uk" className="flex list-disc flex-col gap-1 pl-4 text-[12.5px] text-foreground">
              {suggestion.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </Notice>
        ) : null}

        {suggestion ? <CriteriaList criteria={suggestion.criteria} defaultOpen={suggestion.status === "conflict"} /> : null}

        <section className="rounded-lg bg-muted/50 px-3.5 py-3">
          <h4 className="text-[12px] font-semibold text-muted-foreground">{t.box31.otherItems}</h4>
          <ul className="mt-1.5 flex flex-col gap-1 text-[12.5px] leading-relaxed text-foreground/85">
            {t.box31.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <DialogFooter>
          {entry?.generatedAt ? <span className="text-[12px] text-muted-foreground sm:mr-auto">{t.box31.generatedAt(fmt.dateTime(entry.generatedAt))}</span> : null}
          {!readOnly && suggestion ? (
            <Button variant="outline" size="sm" disabled={busy} onClick={onGenerate}>
              <RefreshCwIcon aria-hidden />
              {t.box31.regenerate}
            </Button>
          ) : null}
          <Button size="sm" onClick={() => onOpenChange(false)}>
            {t.common.close}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The state of the texts in one line of the card. */
function CardState({ entry, generation }: { entry: Box31Entry | null; generation: Box31Generation | null }) {
  const { t, fmt } = useI18n();
  const running = generation !== null && isActiveStatus(generation.status);
  const now = useNow(generation?.status === "running");
  const suggestion = entry?.suggestion ?? null;
  if (running) {
    return (
      <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
        <LoaderCircleIcon className="size-3.5 animate-spin text-primary" aria-hidden />
        {generation.status === "queued" ? t.box31.queued : t.box31.generating}
        {generation.status === "running" ? <span className="code-mono">{fmt.clock(elapsedMs(generation.startedAt, null, now) ?? 0)}</span> : null}
      </span>
    );
  }
  if (!suggestion) {
    return generation && generation.status !== "succeeded" ? (
      <Badge variant="danger">{t.box31.failedBadge}</Badge>
    ) : (
      <span className="truncate text-[12px] text-muted-foreground">{t.box31.cardHint}</span>
    );
  }
  const missing = suggestion.placeholders.filter((placeholder) => placeholder.required).length;
  return (
    <>
      {suggestion.status === "conflict" ? (
        <Badge variant="danger">{t.box31.conflictBadge}</Badge>
      ) : (
        <span className="text-[12px] text-muted-foreground">{t.box31.versions}</span>
      )}
      {missing > 0 ? <Badge variant="warning">{t.box31.needsFacts(missing)}</Badge> : null}
      {entry?.outdated ? <Badge variant="warning">{t.box31.outdated}</Badge> : null}
    </>
  );
}

/**
 * «Графа 31» on a code card: the state of the texts and one button. Without texts the button writes them and opens the
 * dialog; with texts it opens them. A running generation is followed on the turn events, and the session is read again
 * when it ends. The admin view only shows texts that exist.
 */
export function Box31Block({ detail, code, readOnly, className }: { detail: SessionDetail; code: string; readOnly: boolean; className?: string }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const sessionId = detail.session.id;
  const entry = detail.box31.find((item) => item.code === code) ?? null;
  const [open, setOpen] = useState(false);
  const active = entry?.generation && isActiveStatus(entry.generation.status) ? entry.generation : null;
  const live = useTurnStatus(readOnly ? null : (active?.turnId ?? null), (turn) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.session(sessionId) });
    if (turn?.status === "succeeded" && !open) {
      toast.success(t.box31.ready, { description: formatCode(code) });
    }
  });
  const generation: Box31Generation | null = active
    ? { ...active, status: live?.status ?? active.status, startedAt: live?.startedAt ?? active.startedAt, error: live?.error ?? active.error }
    : (entry?.generation ?? null);

  const start = useMutation({
    mutationFn: () => api.startBox31(sessionId, { code }),
    onSuccess: (next) => {
      queryClient.setQueryData<SessionDetail>(queryKeys.session(sessionId), (current) =>
        current ? { ...current, box31: [...current.box31.filter((item) => item.code !== next.code), next] } : current,
      );
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  const generate = () => {
    setOpen(true);
    start.mutate();
  };

  if (readOnly && !entry?.suggestion) return null;
  const opens = entry?.suggestion || generation;

  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <FileTextIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[13px] font-semibold">{t.box31.title}</span>
        <CardState entry={entry} generation={generation} />
      </div>
      <Button
        variant={opens ? "outline" : "soft"}
        size="xs"
        disabled={!opens && start.isPending}
        aria-label={`${opens ? t.box31.open : t.box31.generate}: ${t.box31.titleFor(formatCode(code))}`}
        onClick={() => (opens ? setOpen(true) : generate())}
      >
        {opens ? t.box31.open : t.box31.generate}
      </Button>
      <Box31Dialog
        open={open}
        onOpenChange={setOpen}
        code={code}
        entry={entry}
        generation={generation}
        progress={active && live ? { phase: live.phase, steps: live.steps } : null}
        readOnly={readOnly}
        onGenerate={() => start.mutate()}
        starting={start.isPending}
      />
    </div>
  );
}
